import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, Loader2, Network } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export const GroupBillingCard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [cycle, setCycle] = useState('annual');
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/group-billing/status');
            setData(res.data);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const sessionId = searchParams.get('billing_session_id');
        if (!sessionId) return;
        let attempts = 0;
        setPolling(true);
        const poll = async () => {
            attempts += 1;
            try {
                const res = await api.get(`/group-billing/checkout/status/${sessionId}`);
                if (res.data.payment_status === 'paid') {
                    toast.success('Abonnement Vue Groupe activé !');
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    load();
                    return;
                }
                if (res.data.status === 'expired') {
                    toast.error('Session de paiement expirée');
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch { /* retry */ }
            if (attempts < 8) setTimeout(poll, 2500);
            else { setPolling(false); toast.info('Vérification du paiement en cours, revenez dans un instant.'); }
        };
        poll();
    }, [searchParams, setSearchParams, load]);

    if (!data) return null;
    const sub = data.subscription || {};
    const pkg = data.packages?.[cycle] || {};
    const setupDue = cycle === 'monthly' && !sub.setup_fee_paid;
    const total = (pkg.amount || 0) + (setupDue ? (pkg.setup_fee || 0) : 0);

    const checkout = async () => {
        setLoading(true);
        try {
            const res = await api.post('/group-billing/checkout', { cycle, origin_url: window.location.origin });
            window.location.href = res.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création du paiement');
            setLoading(false);
        }
    };

    return (
        <Card className="bg-[#0c0814]/80 border-white/10 p-5 sm:p-6" data-testid="group-billing-card">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A855F7] to-[#FF914D] flex items-center justify-center">
                    <Network className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h3 className="text-white font-bold">Abonnement Vue Groupe</h3>
                    <p className="text-xs text-white/40">249 € HT/mois — en complément des abonnements de vos agences</p>
                </div>
            </div>

            {polling && (
                <div className="flex items-center gap-2 text-sm text-white/70 py-4" data-testid="group-billing-polling">
                    <Loader2 className="w-4 h-4 animate-spin" /> Vérification du paiement…
                </div>
            )}

            {!polling && sub.active && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="group-billing-active">
                    <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-green-400">Abonnement actif ({sub.cycle === 'annual' ? 'annuel' : 'mensuel'})</p>
                        <p className="text-xs text-white/50 mt-1">
                            Valable jusqu'au {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('fr-FR') : '—'}
                        </p>
                    </div>
                </div>
            )}

            {!polling && !sub.active && (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        {['monthly', 'annual'].map((c) => (
                            <button
                                key={c}
                                onClick={() => setCycle(c)}
                                data-testid={`group-billing-cycle-${c}`}
                                className={`p-4 rounded-xl border text-left transition-colors ${
                                    cycle === c ? 'border-[#A855F7] bg-[#A855F7]/10' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                                }`}
                            >
                                <p className="text-xs uppercase tracking-wider text-white/40">{c === 'monthly' ? 'Mensuel' : 'Annuel (-10%)'}</p>
                                <p className="text-xl font-black text-white mt-1">
                                    {data.packages[c].amount.toLocaleString('fr-FR')}€ <span className="text-xs font-normal text-white/40">HT/{c === 'monthly' ? 'mois' : 'an'}</span>
                                </p>
                                <p className="text-[11px] text-white/40 mt-1">
                                    {c === 'monthly'
                                        ? (sub.setup_fee_paid ? 'Installation déjà réglée' : '+ 149€ d\'installation (1re fois)')
                                        : 'Installation offerte'}
                                </p>
                            </button>
                        ))}
                    </div>
                    <Button
                        onClick={checkout}
                        disabled={loading}
                        data-testid="group-billing-checkout-btn"
                        className="w-full bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white font-bold hover:opacity-90"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                        Souscrire — {total.toLocaleString('fr-FR')}€ HT
                    </Button>
                    <p className="text-[11px] text-white/30 mt-2 text-center">Paiement sécurisé Stripe. Comparatif d'agences, alertes, Providence IA et exports inclus.</p>
                </>
            )}
        </Card>
    );
};

export default GroupBillingCard;
